import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";

import useDebounce from "../../hooks/useDebounce";
import { getMangoCreditTransactions } from "../api";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const transactionTypeOptions = [
  { label: "Credit Addition", value: "credit_addition" },
  { label: "Credit Deduction", value: "credit_deduction" },
];

const statusOptions = [
  { label: "All Statuses", value: "" },
  { label: "Completed", value: "completed" },
  { label: "Initiated", value: "initiated" },
];

const countryOptions = [
  { label: "Indian", value: "indian" },
  { label: "International", value: "international" },
];

const MangoCreditTransactions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);
  const [transactionType, setTransactionType] = useState("credit_addition");
  const [status, setStatus] = useState("completed");
  const [country, setCountry] = useState("");
  const [tableKey, setTableKey] = useState(Date.now());

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => setPage(1),
  });

  const getTransactionsData = async (csvDownload = false) => {
    setLoading(true);
    try {
      const startDate = dateRange?.[0]?.toISOString() || "";
      const endDate = dateRange?.[1]?.toISOString() || "";

      const resp = await getMangoCreditTransactions(
        startDate,
        endDate,
        debouncedSearch,
        transactionType,
        status,
        csvDownload ? undefined : page,
        csvDownload ? undefined : limit,
        csvDownload,
        country
      );

      if (csvDownload) {
        fileDownload(
          resp,
          `mango-credit-transactions-${moment().format("YYYY-MM-DD")}.csv`
        );

        return;
      }

      if (resp?.code === 200) {
        setData(resp.result?.data || []);
        setData(resp.result?.data || []);
        setTotal(resp.result?.total || 0);
        setTableKey(Date.now());
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTransactionsData();
  }, [
    page,
    limit,
    debouncedSearch,
    dateRange,
    transactionType,
    status,
    country,
  ]);

  const columns = [
    {
      title: "Transaction ID",
      dataIndex: "_id",
      key: "_id",
      width: 220,
      render: (text) => (
        <Typography.Text copyable style={{ wordBreak: "break-all" }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user) => (
        <div>
          <Typography.Link href={`/user-details/${user?._id}`}>
            {user?.name}
          </Typography.Link>
          <div style={{ fontSize: "12px", marginTop: "4px" }}>
            {user?.email && <div>{user?.email}</div>}
            {user?.phone && <div>{user?.phone}</div>}
          </div>
        </div>
      ),
    },
    {
      title: "Transaction Type",
      dataIndex: "transactionType",
      key: "transactionType",
      render: (text) => {
        const colorMap = {
          credit_addition: "green",
          credit_deduction: "blue",
        };
        return <Tag color={colorMap[text] || "default"}>{text}</Tag>;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => (
        <span>
          {text ? `${record.currency || "INR"} ${text.toFixed(2)}` : "-"}
        </span>
      ),
    },
    {
      title: "Credits",
      dataIndex: "credits",
      key: "credits",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        const colorMap = {
          completed: "success",
          initiated: "warning",
        };
        return <Tag color={colorMap[text] || "default"}>{text}</Tag>;
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      title: "Razorpay ID",
      dataIndex: "razorpayPaymentId",
      key: "razorpayPaymentId",
      render: (text) => text || "-",
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Title level={2}>Mango Credit Transactions</Title>

      <Card style={{ marginBottom: "20px" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Date Range</Text>
            </div>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={setDateRange}
              allowClear={false}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Transaction Type</Text>
            </div>
            <Select
              style={{ width: "100%" }}
              value={transactionType}
              onChange={setTransactionType}
              options={transactionTypeOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Status</Text>
            </div>
            <Select
              style={{ width: "100%" }}
              value={status}
              onChange={setStatus}
              options={statusOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Country</Text>
            </div>
            <Select
              style={{ width: "100%" }}
              value={country}
              onChange={setCountry}
              options={countryOptions}
              placeholder="All Countries"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Search</Text>
            </div>
            <Search
              placeholder="Search by user name, email, transaction ID"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Export</Text>
            </div>
            <Button
              type="primary"
              onClick={() => getTransactionsData(true)}
              loading={loading}
            >
              Download CSV
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          onChange: (newPage) => setPage(newPage),
          onShowSizeChange: (_, newSize) => {
            setLimit(newSize);
            setPage(1);
          },
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (totalCount) => `Total ${totalCount} transactions`,
        }}
        loading={loading}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default MangoCreditTransactions;
